import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

import { peripheralService } from "/app/src/services";

import Search from "../search";
import { useTranslation } from "react-i18next";
import { Peripheral as PeripheralType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
/**
 * Component for displaying all peripherals
 */
export default function Peripherals() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useSortUpgrade([]);
  const [searchString, setSearchString] = useState("");

  const columnHelper = createColumnHelper<PeripheralType>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        cell: (info) => info.getValue(),
        header: () => t("translation:name"),
      }),
      columnHelper.accessor("typeDescription", {
        id: "typeDescription",
        cell: (info) => info.getValue(),
        header: () => t("translation:type"),
      }),
      columnHelper.accessor("description", {
        id: "description",
        cell: (info) => info.getValue(),
        header: () => t("translation:description"),
      }),
    ],
    [columnHelper, t],
  );

  const goToPeripheral = useCallback(
    (peripheralId: number) => {
      navigate(`/explorer/peripherals/${peripheralId}`);
    },
    [navigate],
  );

  const {
    data: peripherals,
    count: peripheralsCount,
    isFetching,
  } = usePaginatedData<PeripheralType>({
    queryKey: ["peripherals"],
    page,
    pageSize,
    searchString,
    sort,
    service: peripheralService,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:peripherals")} - ItemPath</title>
      </Helmet>
      <div className="search box">
        <Search setSearchString={setSearchString} />
      </div>
      <Table
        loading={isFetching}
        rows={peripherals}
        tableColumns={columns}
        length={peripheralsCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage,
          setPageSize,
        }}
        enableRowSelection
        rowClicked={goToPeripheral}
        emptyText={t("translation:no_peripherals")}
      />
    </div>
  );
}
